import type { ProblemDebugItem, ProblemDebugItemKind } from '@axilon-apps/domains-ontology-ui-state'

/** Evidence sections, laid out in three columns: what to look at, what to read, the logic and history. */
export const EVIDENCE_COLUMNS: readonly {
  label: string
  kinds: readonly ProblemDebugItemKind[]
}[][] = [
  [
    { label: 'Signals', kinds: ['signal', 'connection'] },
    { label: 'Equipment', kinds: ['equipment'] }
  ],
  [
    { label: 'Manuals & documents', kinds: ['document'] },
    { label: 'Drawings', kinds: ['drawing'] }
  ],
  [
    { label: 'PLC logic', kinds: ['plc_logic'] },
    { label: 'Past fixes', kinds: ['past_fix'] },
    { label: 'Other', kinds: ['other'] }
  ]
]

/** Each column's non-empty sections, keeping arrival order within a section. */
export function evidenceColumns(items: readonly ProblemDebugItem[]) {
  return EVIDENCE_COLUMNS.map((column) =>
    column
      .map((section) => ({
        ...section,
        items: items.filter((item) => section.kinds.includes(item.kind))
      }))
      .filter((section) => section.items.length > 0)
  )
}

const START_HERE_KINDS: readonly (readonly ProblemDebugItemKind[])[] = [
  ['signal', 'connection'],
  ['document'],
  ['plc_logic']
]

/**
 * Up to three places to start: the first signal to check, the first manual
 * to read and the first PLC logic to inspect, filled from the other evidence
 * in arrival order when one of those is missing.
 */
export function startHereItems(items: readonly ProblemDebugItem[]): ProblemDebugItem[] {
  const picked = START_HERE_KINDS.map((kinds) =>
    items.find((item) => kinds.includes(item.kind))
  ).filter((item): item is ProblemDebugItem => item !== undefined)
  for (const item of items) {
    if (picked.length >= 3) break
    if (!picked.includes(item)) picked.push(item)
  }
  return picked.slice(0, 3)
}

/** A value short enough to sit beside an item's title; longer ones stay in the panel. */
export function inlineReading(item: ProblemDebugItem): string | null {
  const value = item.facts?.[0]?.value.trim()
  return value && value.length <= 24 ? value : null
}

export interface PlcCodeLine {
  text: string
  /** Branch nesting, for indentation. */
  depth: number
  /** A branch marker rather than an instruction. */
  marker?: boolean
}

// One ladder instruction (operands may hold one level of parentheses) or a branch token.
const LADDER_TOKEN = /[A-Za-z_][\w.]*\((?:[^()]|\([^()]*\))*\)|[[\],]/g

/**
 * Rung text one instruction per line, indented inside branches, so a long
 * rung reads top to bottom. A line that is not pure ladder text (structured
 * text, comments) is kept as written.
 */
export function plcCodeLines(code: string): PlcCodeLine[] {
  return code.split(/\r?\n/).flatMap((line): PlcCodeLine[] => {
    const tokens = line.match(LADDER_TOKEN) ?? []
    // Rung text ends with ';' in L5X exports.
    const pure = line.replace(LADDER_TOKEN, '').replace(/;/g, '').trim() === ''
    if (!pure || tokens.filter((token) => token.length > 1).length < 2) {
      return line.trim() ? [{ text: line.trimEnd(), depth: 0 }] : []
    }
    const out: PlcCodeLine[] = []
    let depth = 0
    for (const token of tokens) {
      if (token === '[') {
        out.push({ text: 'branch', depth, marker: true })
        depth++
      } else if (token === ',') {
        if (depth > 0) out.push({ text: 'or', depth: depth - 1, marker: true })
      } else if (token === ']') {
        depth = Math.max(0, depth - 1)
        out.push({ text: 'end branch', depth, marker: true })
      } else {
        out.push({ text: token, depth })
      }
    }
    return out
  })
}

export interface RoutineRung {
  number: string
  logic: string
  comment?: string
}

/** A routine's rungs as the PLC parsers store them: an array, or the same array as JSON. */
export function routineRungs(raw: unknown): RoutineRung[] {
  let rows: unknown = raw
  if (typeof raw === 'string') {
    try {
      rows = JSON.parse(raw)
    } catch {
      return []
    }
  }
  if (!Array.isArray(rows)) return []
  return rows.flatMap((row, index): RoutineRung[] => {
    if (!row || typeof row !== 'object') return []
    const { number, logic, comment } = row as Record<string, unknown>
    if (typeof logic !== 'string' || !logic.trim()) return []
    return [
      {
        number:
          typeof number === 'number' || typeof number === 'string' ? String(number) : String(index),
        logic,
        ...(typeof comment === 'string' && comment.trim() ? { comment: comment.trim() } : {})
      }
    ]
  })
}

/** The cited rung with up to `radius` rungs either side, and how many are left out. */
export function rungWindow(rungs: readonly RoutineRung[], cited: string | undefined, radius = 3) {
  const at = rungs.findIndex((rung) => rung.number === cited)
  if (at < 0) return null
  const start = Math.max(0, at - radius)
  const end = Math.min(rungs.length, at + radius + 1)
  return { rungs: rungs.slice(start, end), before: start, after: rungs.length - end }
}
